import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, Animated, Image } from 'react-native';
import SvgIcon from './SvgIcon';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { useNavigationVisibility } from '../context/NavigationVisibilityContext';
import colors from '../theme/colors';

export default function Header() {
  const navigation = useNavigation();
  const { user, logout, isAuthenticated } = useAuth();
  const { headerAnimation } = useNavigationVisibility();
  const currentRoute = useNavigationState(state => 
    state?.routes?.[state.index]?.name || 'Home'
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [slideAnim] = useState(new Animated.Value(-280));

  const toggleMenu = () => {
    if (menuOpen) {
      Animated.timing(slideAnim, {
        toValue: -280,
        duration: 300,
        useNativeDriver: true,
      }).start(() => setMenuOpen(false));
    } else {
      setMenuOpen(true);
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  };

  const handleNavigation = (screen) => {
    navigation.navigate(screen);
    toggleMenu();
  };

  // Different menu items based on user type
  const getMenuItems = () => {
    if (isAuthenticated && user?.userType === 'Mediator') {
      return [
        { name: 'Profiles', icon: 'people', screen: 'Profiles' },
        { name: 'Features', icon: 'sparkles', screen: 'Features' },
        { name: 'How it works', icon: 'git-network', screen: 'HowItWorks' },
        { name: 'Success Stories', icon: 'heart', screen: 'SuccessStories' },
      ];
    } else if (isAuthenticated && user?.userType === 'Member') {
      return [
        { name: 'My Profile', icon: 'person', screen: 'MyProfile' },
        { name: 'Shared Profiles', icon: 'people', screen: 'SharedProfiles' },
        { name: 'Features', icon: 'sparkles', screen: 'Features' },
        { name: 'How it works', icon: 'git-network', screen: 'HowItWorks' },
        { name: 'Success Stories', icon: 'heart', screen: 'SuccessStories' },
      ];
    } else {
      return [
        { name: 'Home', icon: 'home', screen: 'Home' },
        { name: 'Features', icon: 'sparkles', screen: 'Features' },
        { name: 'How it works', icon: 'git-network', screen: 'HowItWorks' },
        { name: 'Success Stories', icon: 'heart', screen: 'SuccessStories' },
      ];
    }
  };

  const menuItems = getMenuItems();

  const getUserInitials = () => {
    if (!user) {
      console.log('No user data available');
      return '';
    }
    console.log('User data in Header:', user);
    // API returns 'name' and 'surName', not 'firstName' and 'lastName'
    const firstName = user.firstName || user.name || '';
    const lastName = user.lastName || user.surName || '';
    console.log('firstName:', firstName, 'lastName:', lastName);
    const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    console.log('Initials:', initials);
    return initials;
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigation.navigate('Home');
  };

  const handleMemberLogin = () => {
    setLoginModalVisible(false);
    // Navigate to home and trigger member login
    navigation.navigate('Home', { openLogin: 'matchseeker' });
  };

  const handleMediatorLogin = () => {
    setLoginModalVisible(false);
    // Navigate to home and trigger mediator login
    navigation.navigate('Home', { openLogin: 'mediator' });
  };

  return (
    <>
      <Animated.View style={[styles.header, { transform: [{ translateY: headerAnimation }] }]}>
        <View style={styles.headerContent}>
          {/* Hamburger Menu Icon */}
          <TouchableOpacity 
            style={styles.hamburger} 
            onPress={toggleMenu}
          >
            <View style={[styles.hamburgerLine, menuOpen && styles.hamburgerLineActive]} />
            <View style={[styles.hamburgerLine, menuOpen && styles.hamburgerLineHidden]} />
            <View style={[styles.hamburgerLine, menuOpen && styles.hamburgerLineActive]} />
          </TouchableOpacity>
          
          <View style={styles.logoContainer}>
            <Image 
              source={require('../assets/km-logo.png')} 
              style={styles.logoImage}
              resizeMode="contain"
            />
            <Text style={styles.logo}>
              <Text style={styles.logoKalyana}>Kalyana</Text>
              <Text style={styles.logoMitra}>Mitra</Text>
            </Text>
          </View>
          
          {isAuthenticated ? (
            <View>
              <TouchableOpacity 
                style={styles.userAvatar}
                onPress={() => setUserMenuOpen(!userMenuOpen)}
              >
                <Text style={styles.userInitials}>{getUserInitials()}</Text>
              </TouchableOpacity>
              
              {userMenuOpen && (
                <>
                  <TouchableOpacity 
                    style={styles.userMenuOverlay}
                    activeOpacity={1}
                    onPress={() => setUserMenuOpen(false)}
                  />
                  <View style={styles.userMenu}>
                    <View style={styles.userMenuHeader}>
                      <Text style={styles.userMenuName} numberOfLines={1}>
                        {user?.firstName || user?.name} {user?.lastName || user?.surName}
                      </Text>
                      <Text style={styles.userMenuEmail}>{user?.email || user?.mobileNumber}</Text>
                    </View>
                    <TouchableOpacity 
                      style={styles.userMenuItem}
                      onPress={() => {
                        setUserMenuOpen(false);
                        navigation.navigate('ChangePassword');
                      }}
                    >
                      <SvgIcon name="lock-closed-outline" size={20} color={colors.white} />
                      <Text style={styles.userMenuItemText}>Change Password</Text>
                    </TouchableOpacity>
                    <TouchableOpacity 
                      style={styles.userMenuItem}
                      onPress={handleLogout}
                    >
                      <SvgIcon name="log-out" size={20} color={colors.white} />
                      <Text style={styles.userMenuItemText}>Logout</Text>
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </View>
          ) : (
            <TouchableOpacity 
              style={styles.loginButton}
              onPress={() => setLoginModalVisible(true)}
            >
              <SvgIcon name="person" size={20} color={colors.white} />
            </TouchableOpacity>
          )}
          
        </View>
      </Animated.View>
      
      {/* Overlay and Menu */}
      {menuOpen && (
        <>
          <TouchableOpacity 
            style={styles.overlay}
            activeOpacity={1}
            onPress={toggleMenu}
          />
          <Animated.View 
            style={[
              styles.mobileMenu,
              {
                transform: [{ translateX: slideAnim }]
              }
            ]}
          >
            <View style={styles.menuHeader}>
              <View style={styles.menuTitleContainer}>
                <Image 
                  source={require('../assets/km-logo.png')} 
                  style={styles.menuLogo}
                  resizeMode="contain"
                />
                <View>
                  <Text style={styles.menuTitle}>
                    <Text style={styles.menuTitleKalyana}>Kalyana</Text>
                    <Text style={styles.menuTitleMitra}>Mitra</Text>
                  </Text>
                  <Text style={styles.menuSubtitle}>Simplifying the Marriage Journey</Text>
                </View>
              </View>
              <TouchableOpacity 
                style={styles.menuCloseButton}
                onPress={toggleMenu}
              >
                <SvgIcon name="close" size={28} color={colors.white} />
              </TouchableOpacity>
            </View>
            
            <View style={styles.menuSection}>
              {menuItems.map((item) => {
                const isActive = currentRoute === item.screen;
                return (
                  <TouchableOpacity 
                    key={item.screen}
                    style={[styles.mobileMenuItem, isActive && styles.menuItemActive]} 
                    onPress={() => handleNavigation(item.screen)}
                  >
                    <SvgIcon 
                      name={item.icon} 
                      size={22} 
                      color={isActive ? colors.secondary : colors.white} 
                      style={styles.menuItemIcon} 
                    />
                    <Text style={[styles.mobileMenuText, isActive && styles.mobileMenuTextActive]}>
                      {item.name}
                    </Text>
                  </TouchableOpacity>
                );
              })}
              <TouchableOpacity style={styles.mobileMenuItem} onPress={toggleMenu}>
                <SvgIcon name="mail" size={22} color={colors.white} style={styles.menuItemIcon} />
                <Text style={styles.mobileMenuText}>Contact Us</Text>
              </TouchableOpacity>
            </View>
          </Animated.View>
        </>
      )}
      
      {/* Login Selection Modal */}
      <Modal
        visible={loginModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setLoginModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Welcome to KalyanaMitra</Text>
              <TouchableOpacity 
                style={styles.closeButton}
                onPress={() => setLoginModalVisible(false)}
              >
                <SvgIcon name="close" size={20} color={colors.white} />
              </TouchableOpacity>
            </View>
            
            <View style={styles.modalBody}>
              <Text style={styles.modalSubtitle}>Choose your account type</Text>
              
              {/* Mediator Card */}
              <TouchableOpacity 
                style={[styles.accountCard, styles.mediatorCard]}
                onPress={handleMediatorLogin}
              >
                <View style={styles.cardHeader}>
                  <View style={[styles.iconContainer, styles.mediatorIconBg]}>
                    <SvgIcon name="person-circle" size={32} color={colors.white} />
                  </View>
                  <View style={styles.cardHeaderText}>
                    <Text style={styles.cardTitle}>Mediator</Text>
                    <Text style={styles.cardSubtitle}>Help others find their match</Text>
                  </View>
                </View>
                
                <View style={styles.featureList}>
                  <View style={styles.featureItem}>
                    <SvgIcon name="checkmark-circle" size={16} color={colors.secondary} />
                    <Text style={styles.featureText}>Manage profiles</Text>
                  </View>
                  <View style={styles.featureItem}>
                    <SvgIcon name="checkmark-circle" size={16} color={colors.secondary} />
                    <Text style={styles.featureText}>Create connections</Text>
                  </View>
                  <View style={styles.featureItem}>
                    <SvgIcon name="checkmark-circle" size={16} color={colors.secondary} />
                    <Text style={styles.featureText}>Track success</Text>
                  </View>
                </View>
              </TouchableOpacity>
              
              {/* Match Seeker Card */}
              <TouchableOpacity 
                style={[styles.accountCard, styles.seekerCard]}
                onPress={handleMemberLogin}
              >
                <View style={styles.cardHeader}>
                  <View style={[styles.iconContainer, styles.seekerIconBg]}>
                    <SvgIcon name="people" size={32} color={colors.white} />
                  </View>
                  <View style={styles.cardHeaderText}>
                    <Text style={styles.cardTitle}>Match-Seeker</Text>
                    <Text style={styles.cardSubtitle}>Looking for your life partner</Text>
                  </View>
                </View>
                
                <View style={styles.featureList}>
                  <View style={styles.featureItem}>
                    <SvgIcon name="checkmark-circle" size={16} color={colors.secondary} />
                    <Text style={styles.featureText}>Browse verified profiles</Text>
                  </View>
                  <View style={styles.featureItem}>
                    <SvgIcon name="checkmark-circle" size={16} color={colors.secondary} />
                    <Text style={styles.featureText}>Connect with matches</Text>
                  </View>
                  <View style={styles.featureItem}>
                    <SvgIcon name="checkmark-circle" size={16} color={colors.secondary} />
                    <Text style={styles.featureText}>Consult mediators</Text>
                  </View>
                </View>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  header: {
    position: 'absolute',
    top: 24,
    left: 0,
    right: 0,
    backgroundColor: '#0F172A', // slate-900
    paddingHorizontal: 16,
    paddingVertical: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 8,
    zIndex: 100,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  hamburger: {
    width: 28,
    height: 24,
    justifyContent: 'space-between',
    padding: 4,
  },
  hamburgerLine: {
    width: '100%',
    height: 3,
    backgroundColor: colors.white,
    borderRadius: 2,
  },
  hamburgerLineActive: {
    transform: [{ rotate: '45deg' }],
  },
  hamburgerLineHidden: {
    opacity: 0,
  },
  logoContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  logoImage: {
    width: 32,
    height: 32,
  },
  logo: {
    fontSize: 20,
    fontWeight: '700',
  },
  logoKalyana: {
    color: colors.white,
  },
  logoMitra: {
    color: colors.secondary,
  },
  loginButton: {
    borderWidth: 1,
    borderColor: colors.secondary,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  loginButtonText: {
    color: colors.white,
    fontSize: 12,
    fontWeight: '600',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  },
  mobileMenu: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: 280,
    height: '100vh',
    backgroundColor: colors.primary,
    shadowColor: '#000',
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 1001,
  },
  menuHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 24,
    backgroundColor: colors.primary,
  },
  menuTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  menuLogo: {
    width: 36,
    height: 36,
  },
  menuTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  menuTitleKalyana: {
    color: colors.white,
  },
  menuTitleMitra: {
    color: colors.secondary,
  },
  menuSubtitle: {
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  menuCloseButton: {
    padding: 4,
  },
  menuSection: {
    paddingVertical: 8,
  },
  mobileMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
  },
  menuItemActive: {
    backgroundColor: '#E8F5F4',
  },
  menuItemIcon: {
    marginRight: 16,
    width: 22,
  },
  doubleHeartIcon: {
    position: 'relative',
    width: 22,
    height: 22,
    marginRight: 16,
  },
  heartBack: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  heartFront: {
    position: 'absolute',
    top: 4,
    left: 8,
  },
  mobileMenuText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '500',
  },
  mobileMenuTextActive: {
    color: colors.secondary,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContent: {
    backgroundColor: colors.white,
    borderRadius: 24,
    width: '100%',
    maxWidth: 448,
    maxHeight: '90%',
    overflow: 'hidden',
  },
  modalHeader: {
    backgroundColor: '#0F172A', // slate-900
    paddingHorizontal: 24,
    paddingVertical: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.white,
  },
  closeButton: {
    padding: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
  },
  modalBody: {
    padding: 24,
  },
  modalSubtitle: {
    fontSize: 16,
    color: '#6B7280', // gray-600
    marginBottom: 24,
    textAlign: 'center',
  },
  accountCard: {
    backgroundColor: colors.white,
    borderWidth: 2,
    borderRadius: 16,
    padding: 24,
    marginBottom: 16,
  },
  seekerCard: {
    borderColor: '#EF4444', // red-500
  },
  mediatorCard: {
    borderColor: colors.secondary,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 16,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  seekerIconBg: {
    backgroundColor: '#EF4444', // red-500
  },
  mediatorIconBg: {
    backgroundColor: colors.secondary,
  },
  cardHeaderText: {
    flex: 1,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827', // gray-900
    marginBottom: 4,
  },
  cardSubtitle: {
    fontSize: 13,
    color: '#6B7280', // gray-600
  },
  featureList: {
    gap: 8,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  featureText: {
    fontSize: 14,
    color: '#6B7280', // gray-600
  },
  userAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.accent,
    justifyContent: 'center',
    alignItems: 'center',
  },
  userInitials: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.white,
  },
  userMenuOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: 1000,
    height: 1000,
    zIndex: 999,
  },
  userMenu: {
    position: 'absolute',
    top: 45,
    right: 0,
    backgroundColor: colors.secondary,
    borderRadius: 8,
    minWidth: 180,
    maxWidth: 280,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    zIndex: 1000,
  },
  userMenuHeader: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: colors.primary,
  },
  userMenuName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: 4,
  },
  userMenuEmail: {
    fontSize: 12,
    color: '#E0E0E0',
  },
  userMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 15,
  },
  userMenuItemText: {
    fontSize: 14,
    color: colors.white,
  },
});